import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';

const DISCLAIMER_SHORT = "HEIR LUXX is an independent personal shopping service. All products are sourced from authorised online retail platforms. We are not affiliated with or endorsed by any featured luxury brand. All brand names and trademarks remain the property of their respective owners.";

const DISCLAIMER_FULL = `HEIR LUXX operates as an independent personal shopper service and is not affiliated with, endorsed by, sponsored by, or in any official partnership with any of the luxury brands, designers, or fashion houses featured on this platform. All brand names, trademarks, logos, and intellectual property displayed remain the exclusive property of their respective owners.

PRODUCT SOURCING & IMAGERY: All products and product images displayed on HEIR LUXX are sourced exclusively from authorised third-party online retail platforms and marketplaces, including but not limited to Cettire, Bloomingdale's, and other established luxury e-commerce retailers. Product images are reproduced solely for the purpose of product identification and customer information. HEIR LUXX does not claim ownership of any brand imagery or product photography. All image rights remain with the originating retailers and/or brand owners.

RESALE & LEGALITY: The procurement and resale of authentic luxury goods purchased through authorised retail channels is lawful under applicable consumer and commercial legislation. All items sourced by HEIR LUXX are genuine, authentic products procured directly from legitimate, authorised online retailers. HEIR LUXX does not sell counterfeit, replica, or grey-market goods.

SERVICE FEES & PRICING: Prices displayed on this platform include a personal shopping service fee charged by HEIR LUXX as compensation for product sourcing, authenticity verification, quality inspection, procurement, and logistics coordination. All prices are subject to live currency conversion rates and may fluctuate. Displayed prices exclude shipping costs and any applicable customs duties or import taxes, which are communicated separately.

LIMITATION OF LIABILITY: HEIR LUXX makes no warranty, express or implied, regarding the accuracy of product descriptions, size availability, stock levels, or pricing as provided by source retailers, nor for any changes made by those retailers after the time of sourcing. HEIR LUXX shall not be liable for any indirect, incidental, or consequential loss arising from reliance on information displayed on this platform.

By accessing this platform and engaging our personal shopping service, you acknowledge that you have read, understood, and agreed to the terms set out in this disclaimer.`;

export default function Footer() {
  const [expanded, setExpanded] = useState(false);

  return (
    <footer className="bg-[#0A0A0A] text-white mt-24">
      {/* Legal Disclaimer */}
      <div className="border-b border-white/10 px-6 lg:px-12 py-8 max-w-7xl mx-auto">
        <button
          onClick={() => setExpanded(v => !v)}
          className="flex gap-3 items-start w-full text-left group"
          data-testid="disclaimer-toggle"
        >
          <Info size={15} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work">Legal Disclaimer</span>
              {expanded
                ? <ChevronUp size={13} className="text-white/40 group-hover:text-[#D4AF37] transition-colors" />
                : <ChevronDown size={13} className="text-white/40 group-hover:text-[#D4AF37] transition-colors" />
              }
            </div>
            <p className="text-[11px] text-white/50 leading-relaxed font-work">
              {expanded ? null : DISCLAIMER_SHORT}
            </p>
          </div>
        </button>

        {expanded && (
          <div className="mt-4 pl-6 border-l border-[#D4AF37]/20">
            {DISCLAIMER_FULL.split('\n\n').map((para) => (
              <p key={para.slice(0, 40)} className="text-[11px] text-white/50 leading-relaxed font-work mb-3 last:mb-0">
                {para}
              </p>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <h3 className="font-playfair text-2xl font-bold tracking-[0.15em] text-white mb-4">HEIR LUXX</h3>
            <p className="text-white/50 text-sm font-work leading-relaxed max-w-xs">
              Curated luxury fashion from the world's finest boutiques, delivered through your personal shopper.
            </p>
          </div>

          {/* Shop */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">Shop</p>
            <ul className="flex flex-col gap-3">
              {[
                { label: "All Products", to: "/shop" },
                { label: "Bags", to: "/shop?category=Bags" },
                { label: "Shoes", to: "/shop?category=Shoes" },
                { label: "Clothing", to: "/shop?category=Clothing" },
                { label: "Accessories", to: "/shop?category=Accessories" },
                { label: "Designers", to: "/designers" },
              ].map(l => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-white/50 hover:text-[#D4AF37] transition-colors font-work">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-5">Account</p>
            <ul className="flex flex-col gap-3">
              {[
                { label: "My Profile", to: "/profile" },
                { label: "Favorites", to: "/favorites" },
                { label: "My Bag", to: "/bag" },
                { label: "Orders", to: "/orders" },
                { label: "Sign In", to: "/auth" },
              ].map(l => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-white/50 hover:text-[#D4AF37] transition-colors font-work">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30 font-work">&copy; {new Date().getFullYear()} HEIR LUXX. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <button onClick={() => setExpanded(true)} className="text-xs text-white/30 hover:text-[#D4AF37] transition-colors font-work underline underline-offset-2">
              Legal Disclaimer
            </button>
            <span className="text-white/20 text-xs">·</span>
            <p className="text-xs text-white/30 font-work">Independent Personal Shopper &middot; Not affiliated with any featured brand</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
